import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { ConfirmationOfApplication } from '@/features/ui/Steps/ConfirmationOfApplication';

import { ConditionView, FormDatePicker, StepControlPanel, useModal } from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { InsuranceOrder } from '@/modules/bia/shared/model/types';

import api from '@/api';

import { getEightDaysFromToday, getOneDayFromToday } from '../../lib/constants';
import { DataPreviewSchema, InsuranceServiceError, InsuranceServiceMergedSchema } from '../../model/types';
import { InsuranceOrderPreviewModal } from '../InsuranceOrderPreviewModal';
import { InsuranceServiceErrorInformer } from '../InsuranceServiceErrorInformer';

import { InsuranceSelectionPreview } from './InsuranceSelection';
import { InsuredAddressPreview } from './InsuredAddress';
import { InsuredDataPreview } from './InsuredData';
import { InsuredDocumentDataPreview } from './InsuredDocumentData';

export interface DataPreviewProps {
  createOrder: (formData: InsuranceServiceMergedSchema) => ReturnType<typeof api.bia.createOrder>;
  shouldBeAbleToUpdateProfile: boolean;
}

export const DataPreview: React.FC<StepProps<DataPreviewProps>> = ({
  createOrder,
  next,
  prev,
  specificStep,
  shouldBeAbleToUpdateProfile,
}) => {
  const { control, watch } = useFormContext<DataPreviewSchema>();
  const { getValues } = useFormContext<InsuranceServiceMergedSchema>();
  const { isOpen, openModal, closeModal } = useModal();

  const confirmEnteredDataCorrect = watch('confirmEnteredDataCorrect');
  const insuranceStartDate = watch('insuranceStartDate');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [insuranceServiceError, setInsuranceServiceError] = useState<Nullable<InsuranceServiceError>>(null);
  const [insuranceOrder, setInsuranceOrder] = useState<Nullable<InsuranceOrder>>(null);

  const handleNextStep = (): void => {
    setIsLoading(true);

    createOrder(getValues())
      .then(({ data }) => data)
      .then(setInsuranceOrder)
      .then(openModal)
      .catch((response) => {
        if (typeof response === 'string') {
          try {
            const responseObj = JSON.parse(response);
            switch (responseObj.responseStatus) {
              case 400:
                setInsuranceServiceError(InsuranceServiceError.SMTH_WENT_WRONG);
                break;

              case 460:
                const messageObject = JSON.parse(responseObj.message);
                const reason = messageObject.reason;
                switch (reason) {
                  case 'PERSONAL_SANCTIONS':
                    setInsuranceServiceError(InsuranceServiceError.FINANCIAL_INTELLIGENCE);
                    break;
                  case 'WRONG_TAX_NUMBER':
                    setInsuranceServiceError(InsuranceServiceError.WRONG_TAX_NUMBER);
                    break;
                  default:
                    setInsuranceServiceError(InsuranceServiceError.SMTH_WENT_WRONG);
                    break;
                }
                break;

              case 502:
                setInsuranceServiceError(InsuranceServiceError.REQUEST_FAILED);
                break;

              default:
                console.error(response);
                break;
            }
          } catch (parseError) {
            console.error('Unexpected response format: ', response);
            setInsuranceServiceError(InsuranceServiceError.SMTH_WENT_WRONG);
          }
        }
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <ConditionView if={!isLoading} else={<Loader centered />}>
      <ConditionView
        if={isEmpty(insuranceOrder)}
        else={
          <InsuranceOrderPreviewModal
            isOpen={isOpen}
            insuranceOrder={insuranceOrder!}
            onContinue={next}
            onClose={() => {
              setInsuranceOrder(null);
              closeModal();
            }}
            onCancel={() => {
              specificStep(2);
            }}
          />
        }
      >
        <ConditionView
          if={!insuranceServiceError}
          else={
            <>
              <InsuranceServiceErrorInformer error={insuranceServiceError} />
              <StepControlPanel
                next={() => {
                  setInsuranceServiceError(null);
                  handleNextStep();
                }}
                nextBtnText="Спробувати ще"
                withArrow={false}
              />
            </>
          }
        >
          <FormBlock title="Підтвердження даних" className="form-block__data-preview">
            <InsuranceSelectionPreview />
            <InsuredDataPreview />
            <InsuredAddressPreview />
            <InsuredDocumentDataPreview />
          </FormBlock>

          <FormDatePicker
            name="insuranceStartDate"
            control={control}
            label="Дата початку договору страхування"
            required
            minDate={getOneDayFromToday()}
            maxDate={getEightDaysFromToday()}
          />

          <ConfirmationOfApplication shouldBeAbleToUpdateProfile={shouldBeAbleToUpdateProfile} />

          <StepControlPanel
            prev={prev}
            next={handleNextStep}
            nextBtnText="Отримати пропозицію"
            withArrow={false}
            disabledNext={!confirmEnteredDataCorrect || isEmpty(insuranceStartDate)}
          />
        </ConditionView>
      </ConditionView>
    </ConditionView>
  );
};
